import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

/**
 * ErrorBoundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the whole app.
 *
 * Features:
 * - Catches errors in rendering, lifecycle methods, and constructors
 * - Provides user-friendly error message
 * - Offers recovery options (refresh, go home)
 * - Logs errors for debugging
 * - Maintains app stability during runtime errors
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Track error count to prevent infinite error loops
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Send error to backend logging service
    this.reportErrorToBackend(error, errorInfo);
  }

  reportErrorToBackend = async (error, errorInfo) => {
    try {
      // Only report in production to avoid dev noise
      if (process.env.NODE_ENV === 'production') {
        const errorReport = {
          message: error.toString(),
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        };

        // Send to backend error logging endpoint
        await fetch('/api/errors/log', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(errorReport)
        }).catch(err => {
          // Fail silently if error reporting fails
          console.warn('Failed to report error to backend:', err);
        });
      }
    } catch (reportError) {
      // Never let error reporting crash the error boundary
      console.warn('Error reporting failed:', reportError);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Prevent infinite error loops by showing minimal UI after 3 errors
      if (this.state.errorCount > 3) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="text-center max-w-md">
              <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Critical Error
              </h1>
              <p className="text-muted-foreground mb-6">
                Multiple errors detected. Please refresh the page.
              </p>
              <Button onClick={this.handleRefresh} size="lg">
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh Page
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full">
            <div className="bg-card rounded-lg shadow-lg border border-border p-8">
              {/* Error Icon */}
              <div className="flex items-center justify-center mb-6">
                <div className="bg-destructive/10 rounded-full p-4">
                  <AlertTriangle className="h-12 w-12 text-destructive" />
                </div>
              </div>

              {/* Error Heading */}
              <h1 className="text-3xl font-bold text-center text-foreground mb-3">
                Oops! Something went wrong
              </h1>

              <p className="text-center text-muted-foreground mb-8">
                We apologize for the inconvenience. An unexpected error occurred while loading this page.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                <Button
                  onClick={this.handleReset}
                  variant="default"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Go to Homepage
                </Button>
              </div>

              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-6 p-4 bg-muted rounded-md border border-border">
                  <summary className="cursor-pointer font-semibold text-foreground mb-2 hover:text-primary transition-colors">
                    Error Details (Development Only)
                  </summary>

                  <div className="mt-3 space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1">Error Message:</p>
                      <pre className="text-xs bg-background p-3 rounded border border-border overflow-x-auto">
                        {this.state.error.toString()}
                      </pre>
                    </div>

                    {this.state.errorInfo && (
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Component Stack:</p>
                        <pre className="text-xs bg-background p-3 rounded border border-border overflow-x-auto max-h-64 overflow-y-auto">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Help Text */}
              <div className="mt-6 pt-6 border-t border-border text-center text-sm text-muted-foreground">
                <p>
                  If this problem persists, please{' '}
                  <a
                    href="/contact"
                    className="text-primary hover:underline font-medium"
                  >
                    contact our support team
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
